import React, { useEffect, useState, useMemo } from "react";
import { Input, Select, Button, Spin, Modal, Table, Typography } from "antd";
import {
  AppstoreOutlined,
  BarsOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import style from "./EmployeeUserList.module.css";
import UserDetailModal from "../Modals/UserDetailModal";
import UserGalleryModal from "../Modals/UserGalleryModal";
import { formatUserListTable } from "../../models/userListModel/utils";
import CreateEmployeeModal from "./CreateEmployeeModal";
import EditEmployeeModal from "./EditEmployeeModal";
import { changeShowCreateModalEv } from "../../models/userListModel";

import UserCardHR from "../UserCardHR/UserCardHR"; // adjust the path as needed

// ✅ Map userGroups to place_ids
const clientPlaceIds = {
  Galereya: "glra12a4a6",
  Azag: "azgb43p6m5",
};

const { Option } = Select;
const { Text } = Typography;

const EmployeeUserList = ({
  userGroups,
  renderCardBody,
  renderTableColumns,
}) => {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [viewType, setViewType] = useState("cards");

  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const placeId = useMemo(() => {
    if (!userGroups || userGroups.length === 0) return null;
    return clientPlaceIds[userGroups.find((g) => clientPlaceIds[g])] || null;
  }, [userGroups]);

  useEffect(() => {
    if (!placeId) return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          "https://5zbcsztftk.execute-api.eu-west-1.amazonaws.com/default/prod-user-list-api",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sort_key: placeId,
              emp_category: "Сотрудник",
            }),
          }
        );
        const result = await response.json();
        const parsed = JSON.parse(result.body);
        const formatted = formatUserListTable(parsed);
        setUserList(formatted);
        console.log("✅ Formatted User List:", formatted);

        const uniqueOffices = [
          ...new Set(formatted.map((u) => u.emp_office).filter(Boolean)),
        ];
        setDepartmentsList(uniqueOffices);
      } catch (err) {
        console.error("Error loading users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [placeId]);

  const filteredUsers = useMemo(() => {
    return userList
      .filter((u) =>
        `${u.user_name} ${u.user_surname}`.toLowerCase().includes(searchTerm)
      )
      .filter(
        (u) =>
          selectedOffices.length === 0 || selectedOffices.includes(u.emp_office)
      )
      .filter((u) => u.emp_category !== "Уволен")
      .sort((a, b) =>
        sortOrder === "asc"
          ? a.user_surname.localeCompare(b.user_surname, "ru")
          : b.user_surname.localeCompare(a.user_surname, "ru")
      );
  }, [userList, searchTerm, selectedOffices, sortOrder]);

  const openUserModal = (user) => {
    setSelectedUser(user);
    setIsUserModalVisible(true);
  };

  const closeUserModal = () => {
    setIsUserModalVisible(false);
    setSelectedUser(null);
  };

  return (
    <div className={style.userList_wrapper}>
      {/* HEADER BLOCK */}
      <div className={style.header_wrapper}>
        <div className={style.header_title}>
          <h1 className={style.userList_title}>Список сотрудников</h1>
        </div>
        <div className={style.header_count}>
          <span className={style.header_label}>Сотрудников: </span>
          <span className={style.header_number}>{userList.length}</span>
        </div>
      </div>

      {/* FILTERS BLOCK */}
      <div className={style.user_list_top_wrapper}>
        <div className={style.top_buttons_wrapper}>
          <Input
            placeholder="Поиск по имени или фамилии"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            style={{ width: "264px" }}
            allowClear
          />

          <Select
            placeholder="Сортировка"
            style={{ width: "210px" }}
            value={sortOrder}
            onChange={(value) => setSortOrder(value)}
          >
            <Option value="asc">А-Я</Option>
            <Option value="desc">Я-А</Option>
          </Select>

          <Select
            mode="multiple"
            placeholder="Выбрать департамент"
            value={selectedOffices}
            onChange={setSelectedOffices}
            allowClear
            style={{ width: "210px" }}
            loading={loading}
          >
            {departmentsList.map((d) => (
              <Option key={d} value={d}>
                {d}
              </Option>
            ))}
          </Select>
        </div>

        <div className={style.view_add_buttons}>
          <div className={style.view_switch_buttons}>
            <Button
              icon={<AppstoreOutlined />}
              type={viewType === "cards" ? "primary" : "default"}
              onClick={() => setViewType("cards")}
            />
            <Button
              icon={<BarsOutlined />}
              type={viewType === "table" ? "primary" : "default"}
              onClick={() => setViewType("table")}
            />
          </div>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Добавить сотрудника
          </Button>
        </div>
      </div>

      <div className={style.table_or_cards_wrapper}>
        {loading ? (
          <Spin tip="Загрузка..." size="large" />
        ) : viewType === "cards" ? (
          // <div className={style.cards_wrapper}>
          //   {filteredUsers.map((user) => (
          //     <div key={user.partition_key} className={style.card}>
          //       <div className={style.card_header}>
          //         <div className={style.card_image}>
          //           <img
          //             src={user.presigned_url || "/default_user_image.png"}
          //             alt="User"
          //             onClick={() =>
          //               Modal.info({
          //                 title: "Просмотр изображения",
          //                 content: (
          //                   <img
          //                     src={
          //                       user.presigned_url || "/default_user_image.png"
          //                     }
          //                     alt="Full"
          //                     style={{
          //                       width: "100%",
          //                       maxHeight: "80vh",
          //                       objectFit: "contain",
          //                     }}
          //                   />
          //                 ),
          //                 width: "50vw",
          //                 centered: true,
          //               })
          //             }
          //             onError={(e) => {
          //               e.target.onerror = null;
          //               e.target.src = "/default_user_image.png";
          //             }}
          //           />
          //         </div>
          //         <div className={style.card_header_text}>
          //           <h3
          //             className={style.card_title}
          //             onClick={() => openUserModal(user)}
          //           >{`${user.user_surname} ${user.user_name}`}</h3>
          //           <div className={style.card_position_label}>Должность:</div>
          //           <div className={style.card_position_value}>
          //             {user.emp_position}
          //           </div>
          //         </div>
          //       </div>
          //       <div className={style.card_divider}></div>
          //       <div className={style.card_body}>{renderCardBody(user)}</div>
          //     </div>
          //   ))}
          // </div>
          <div className={style.cards_wrapper}>
            {filteredUsers.map((user) => (
              <UserCardHR
                key={user.partition_key}
                user={user}
                onNameClick={() => openUserModal(user)}
                onImageClick={() =>
                  Modal.info({
                    title: "Просмотр изображения",
                    content: (
                      <img
                        src={user.presigned_url || "/default_user_image.png"}
                        alt="Full"
                        style={{
                          width: "100%",
                          maxHeight: "80vh",
                          objectFit: "contain",
                        }}
                      />
                    ),
                    width: "50vw",
                    centered: true,
                  })
                }
              />
            ))}
          </div>
        ) : (
          <Table
            dataSource={filteredUsers}
            columns={renderTableColumns(openUserModal)}
            rowKey="partition_key"
            pagination={{ pageSize: 10 }}
            bordered={false}
            className={style.userList_table}
          />
        )}
      </div>

      <UserDetailModal
        visible={isUserModalVisible}
        onClose={closeUserModal}
        user={selectedUser}
        setIsEditModalOpen={setIsEditModalOpen}
      />
      <UserGalleryModal user={selectedUser} />
      <CreateEmployeeModal
        visible={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
      <EditEmployeeModal
        visible={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </div>
  );
};

export default EmployeeUserList;
